/**
 * DebiteurenCrediteuren - D&C Overzicht
 * EXACT zoals Morris debiteuren_crediteuren.html
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, DCData } from '../../services/boekhouding.service';

const DebiteurenCrediteuren = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DCData | null>(null);

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await boekhoudingService.getDebiteurenCrediteuren(huidigJaar);
        setData(result);
      } catch (err) {
        console.error('Error loading D&C:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [huidigJaar]);

  const formatBedrag = (bedrag: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(bedrag || 0);
  };

  const formatDatum = (datum: string): string => {
    if (!datum) return '-';
    return new Date(datum).toLocaleDateString('nl-NL');
  };

  if (loading) {
    return (
      <BoekhoudingLayout title={`Debiteuren & Crediteuren ${huidigJaar}`}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  if (!data) {
    return (
      <BoekhoudingLayout title={`Debiteuren & Crediteuren ${huidigJaar}`}>
        <div className={styles.error}>Kon D&C data niet laden</div>
      </BoekhoudingLayout>
    );
  }

  return (
    <BoekhoudingLayout title={`Debiteuren & Crediteuren ${huidigJaar}`}>
      <div className={styles.dcContainer}>
        {/* Debiteuren Sectie - EXACT Morris */}
        <div className={styles.card}>
          <h3 className={`${styles.dcHeader} ${styles.dcDebiteuren}`}>Debiteuren</h3>
          <table className={styles.dcTable}>
            <thead>
              <tr>
                <th></th>
                <th className={styles.alignRight}>Q1</th>
                <th className={styles.alignRight}>Q2</th>
                <th className={styles.alignRight}>Q3</th>
                <th className={styles.alignRight}>Q4</th>
                <th className={`${styles.alignRight} ${styles.totaalCol}`}>Totaal</th>
              </tr>
            </thead>
            <tbody>
              <tr className={styles.beginSaldoRow}>
                <td>Begin saldo</td>
                <td colSpan={4}></td>
                <td className={`${styles.alignRight} ${styles.totaalCol}`}>{formatBedrag(data.totaal.debiteuren.begin_saldo)}</td>
              </tr>
              <tr>
                <td>Verkoop</td>
                {data.kwartalen.debiteuren.map((q) => (
                  <td key={q.kwartaal} className={styles.alignRight}>{formatBedrag(q.verkoop)}</td>
                ))}
                <td className={`${styles.alignRight} ${styles.totaalCol}`}>{formatBedrag(data.totaal.debiteuren.verkoop)}</td>
              </tr>
              <tr>
                <td>Ontvangen via Bank</td>
                {data.kwartalen.debiteuren.map((q) => (
                  <td key={q.kwartaal} className={styles.alignRight}>{formatBedrag(q.bank_ontvangen)}</td>
                ))}
                <td className={`${styles.alignRight} ${styles.totaalCol}`}>{formatBedrag(data.totaal.debiteuren.bank_ontvangen)}</td>
              </tr>
              <tr>
                <td>Ontvangen via Kas</td>
                {data.kwartalen.debiteuren.map((q) => (
                  <td key={q.kwartaal} className={styles.alignRight}>{formatBedrag(q.kas_ontvangen)}</td>
                ))}
                <td className={`${styles.alignRight} ${styles.totaalCol}`}>{formatBedrag(data.totaal.debiteuren.kas_ontvangen)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className={styles.totaalRow}>
                <td><strong>Eind saldo (openstaand)</strong></td>
                <td colSpan={4}></td>
                <td className={`${styles.alignRight} ${styles.totaalCol}`}><strong>{formatBedrag(data.totaal.debiteuren.openstaand)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Crediteuren Sectie - EXACT Morris */}
        <div className={styles.card}>
          <h3 className={`${styles.dcHeader} ${styles.dcCrediteuren}`}>Crediteuren</h3>
          <table className={styles.dcTable}>
            <thead>
              <tr>
                <th></th>
                <th className={styles.alignRight}>Q1</th>
                <th className={styles.alignRight}>Q2</th>
                <th className={styles.alignRight}>Q3</th>
                <th className={styles.alignRight}>Q4</th>
                <th className={`${styles.alignRight} ${styles.totaalCol}`}>Totaal</th>
              </tr>
            </thead>
            <tbody>
              <tr className={styles.beginSaldoRow}>
                <td>Begin saldo</td>
                <td colSpan={4}></td>
                <td className={`${styles.alignRight} ${styles.totaalCol}`}>{formatBedrag(data.totaal.crediteuren.begin_saldo)}</td>
              </tr>
              <tr>
                <td>Inkoop</td>
                {data.kwartalen.crediteuren.map((q) => (
                  <td key={q.kwartaal} className={styles.alignRight}>{formatBedrag(q.inkoop)}</td>
                ))}
                <td className={`${styles.alignRight} ${styles.totaalCol}`}>{formatBedrag(data.totaal.crediteuren.inkoop)}</td>
              </tr>
              <tr>
                <td>Betaald via Bank</td>
                {data.kwartalen.crediteuren.map((q) => (
                  <td key={q.kwartaal} className={styles.alignRight}>{formatBedrag(q.bank_betaald)}</td>
                ))}
                <td className={`${styles.alignRight} ${styles.totaalCol}`}>{formatBedrag(data.totaal.crediteuren.bank_betaald)}</td>
              </tr>
              <tr>
                <td>Betaald via Kas</td>
                {data.kwartalen.crediteuren.map((q) => (
                  <td key={q.kwartaal} className={styles.alignRight}>{formatBedrag(q.kas_betaald)}</td>
                ))}
                <td className={`${styles.alignRight} ${styles.totaalCol}`}>{formatBedrag(data.totaal.crediteuren.kas_betaald)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className={styles.totaalRow}>
                <td><strong>Eind saldo (openstaand)</strong></td>
                <td colSpan={4}></td>
                <td className={`${styles.alignRight} ${styles.totaalCol}`}><strong>{formatBedrag(data.totaal.crediteuren.openstaand)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Openstaande Debiteuren - EXACT Morris */}
        {data.openstaande_debiteuren.length > 0 && (
          <div className={styles.card}>
            <h3 className={`${styles.dcHeader} ${styles.dcDebiteuren}`}>Openstaande Debiteuren</h3>
            <table className={`${styles.dcTable} ${styles.facturenTable}`}>
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Factuurnummer</th>
                  <th>Klant</th>
                  <th className={styles.alignRight}>Bedrag</th>
                  <th className={styles.alignRight}>Betaald</th>
                  <th className={styles.alignRight}>Openstaand</th>
                </tr>
              </thead>
              <tbody>
                {data.openstaande_debiteuren.map((f) => (
                  <tr
                    key={f.id}
                    className={styles.clickableRow}
                    onClick={() => navigate(`/boekhouding/boeking/${f.id}`)}
                  >
                    <td>{formatDatum(f.datum)}</td>
                    <td>{f.boekstuknummer || '-'}</td>
                    <td>{f.omschrijving}</td>
                    <td className={styles.alignRight}>{formatBedrag(f.bedrag)}</td>
                    <td className={styles.alignRight}>{formatBedrag(f.betaald)}</td>
                    <td className={styles.alignRight}><strong>{formatBedrag(f.openstaand)}</strong></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={styles.totaalRow}>
                  <td colSpan={3}><strong>Totaal</strong></td>
                  <td className={styles.alignRight}>
                    <strong>{formatBedrag(data.openstaande_debiteuren.reduce((sum, f) => sum + f.bedrag, 0))}</strong>
                  </td>
                  <td className={styles.alignRight}>
                    <strong>{formatBedrag(data.openstaande_debiteuren.reduce((sum, f) => sum + f.betaald, 0))}</strong>
                  </td>
                  <td className={styles.alignRight}>
                    <strong>{formatBedrag(data.openstaande_debiteuren.reduce((sum, f) => sum + f.openstaand, 0))}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Openstaande Crediteuren - EXACT Morris */}
        {data.openstaande_crediteuren.length > 0 && (
          <div className={styles.card}>
            <h3 className={`${styles.dcHeader} ${styles.dcCrediteuren}`}>Openstaande Crediteuren</h3>
            <table className={`${styles.dcTable} ${styles.facturenTable}`}>
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Factuurnummer</th>
                  <th>Leverancier</th>
                  <th className={styles.alignRight}>Bedrag</th>
                  <th className={styles.alignRight}>Betaald</th>
                  <th className={styles.alignRight}>Openstaand</th>
                </tr>
              </thead>
              <tbody>
                {data.openstaande_crediteuren.map((f) => (
                  <tr
                    key={f.id}
                    className={styles.clickableRow}
                    onClick={() => navigate(`/boekhouding/boeking/${f.id}`)}
                  >
                    <td>{formatDatum(f.datum)}</td>
                    <td>{f.boekstuknummer || '-'}</td>
                    <td>{f.omschrijving}</td>
                    <td className={styles.alignRight}>{formatBedrag(f.bedrag)}</td>
                    <td className={styles.alignRight}>{formatBedrag(f.betaald)}</td>
                    <td className={styles.alignRight}><strong>{formatBedrag(f.openstaand)}</strong></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={styles.totaalRow}>
                  <td colSpan={3}><strong>Totaal</strong></td>
                  <td className={styles.alignRight}>
                    <strong>{formatBedrag(data.openstaande_crediteuren.reduce((sum, f) => sum + f.bedrag, 0))}</strong>
                  </td>
                  <td className={styles.alignRight}>
                    <strong>{formatBedrag(data.openstaande_crediteuren.reduce((sum, f) => sum + f.betaald, 0))}</strong>
                  </td>
                  <td className={styles.alignRight}>
                    <strong>{formatBedrag(data.openstaande_crediteuren.reduce((sum, f) => sum + f.openstaand, 0))}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </BoekhoudingLayout>
  );
};

export default DebiteurenCrediteuren;
